import type { RefObject } from "react";

import {
  Divider,
  Flex,
  Input,
  isNullish,
  LabelWrapper,
  Radio,
  Select,
  Switch
} from "@theorgchartdev/storybook";
import { ExportModalMode } from "../ExportModal";
import { FieldInputLabel } from "@/appComponents/Blocks/Composites/FieldInputLabel";
import { GetWSMgr } from "@/Utils/StateLib";
import type { Scope, ScopeController } from "../PreviewCanvas/usePreviewDocument";
import type { TabProps } from "./DocumentExportContent";
import { usePermissionCheck } from "@/hooks/usePermissionCheck";
import { blsRestrictedScopes, hasInvalidFilenameChars } from "../ExportModal.utils";
import { buildPageSizes } from "@/appComponents/Navigation/RightSidebar/PageProperties/utils";
import { isInPlanning } from "@coreapp/utils/isInPlanning";
import { appstr } from "@coreapp/utils/appstr";
import { enumPageOrientationOptions, enumPageSizes, kCMI } from "@coreapp/constants";
import { QXWSMgr } from "@coreapp/CoreApp/QXWSMgr";

type Props = TabProps & {
  scopeControllerRef: RefObject<ScopeController>;
  isIncludeBackgroundDisabled: boolean;
};

export const DocumentExportGeneral = (props: Props) => {
  const { hasActiveBLS } = usePermissionCheck();
  const { fieldData, onFieldChange } = props.formController;

  const inPlanning = isInPlanning();
  const filenameError = hasInvalidFilenameChars(fieldData.name);

  const handleScopeChange = (value: Scope) => {
    onFieldChange("scope", value);
  };

  const scopeOptions = buildScopeOptions(
    handleScopeChange,
    props.scopeControllerRef.current,
    hasActiveBLS
  );
  const areScopesRestricted = scopeOptions.some(({ disabled }) => disabled);

  return (
    <Flex direction="column" gap="6px" padding="6px 24px 0">
      <Input
        autoFocus={true}
        label={appstr("File Name")}
        data-testid="export-modal-input-name"
        placeholder={`${appstr("Enter")} ${appstr("File Name").toLocaleLowerCase()}`}
        size="medium"
        onChange={(_, value) => onFieldChange("name", value)}
        value={fieldData.name}
        status={filenameError ? "error" : "default"}
        message={filenameError ? appstr("exportFilenameInvalidChars") : undefined}
      />
      <Divider />
      <Select
        dataTestId="export-modal-select-scope"
        inputOnly={false}
        selectSize="medium"
        label={appstr("Scope")}
        labelInfo={
          areScopesRestricted
            ? appstr(
                "One or more scope options is disabled because they exceed the maximum box limit for custom charts"
              )
            : undefined
        }
        options={scopeOptions}
        value={fieldData.scope}
      />
      <Select
        inputOnly={false}
        selectSize="medium"
        label={appstr("Page size")}
        options={buildPageSizeOptions(props.mode, fieldData.scope, nextPageSize => {
          onFieldChange("pageSize", nextPageSize);
        })}
        value={fieldData.pageSize}
      />
      <LabelWrapper label={appstr("Orientation")}>
        <Flex direction="column" gap="8px">
          <Radio
            id="export-page-orientation-landscape"
            label={appstr("landscape")}
            checked={fieldData.pageOrientation === enumPageOrientationOptions.landscape}
            onChange={() => onFieldChange("pageOrientation", enumPageOrientationOptions.landscape)}
            disabled={fieldData.pageSize === "current"}
          />
          <Radio
            id="export-page-orientation-portrait"
            label={appstr("portrait")}
            checked={fieldData.pageOrientation === enumPageOrientationOptions.portrait}
            onChange={() => onFieldChange("pageOrientation", enumPageOrientationOptions.portrait)}
            disabled={fieldData.pageSize === "current"}
          />
        </Flex>
      </LabelWrapper>
      <Flex direction="column" gap="12px">
        <FieldInputLabel
          id="document-export-include-background-switch"
          label={appstr("Include Master")}
          helper={appstr(
            "Include elements on the background, such as logos, legends, tables, and page numbers"
          )}
        >
          <Switch
            disabled={props.isIncludeBackgroundDisabled}
            id="document-export-include-background-switch"
            onChange={nextChecked => onFieldChange("includeBackground", nextChecked)}
            checked={fieldData.includeBackground}
          />
        </FieldInputLabel>
        <FieldInputLabel
          id="document-export-white-background-switch"
          label={appstr("excludebackgrnd")}
          helper={appstr(
            "Use a white background instead of a color. This option is helpful if you plan to print this file"
          )}
        >
          <Switch
            id="document-export-white-background-switch"
            onChange={nextChecked => onFieldChange("whiteBackground", nextChecked)}
            checked={fieldData.whiteBackground}
          />
        </FieldInputLabel>
      </Flex>
      {inPlanning && (
        <>
          <Divider />
          <Select
            disabled={!fieldData.includeBackground}
            inputOnly={false}
            selectSize="medium"
            label={appstr("Show Activity")}
            options={buildActivityOptions(value => onFieldChange("showActivity", value))}
            value={fieldData.showActivity}
          />
          <Flex direction="column" gap="12px">
            <FieldInputLabel
              id="document-export-show-overlay-switch"
              label={appstr("Show Overlay")}
            >
              <Switch
                id="document-export-show-overlay-switch"
                onChange={nextChecked => onFieldChange("showOverlay", nextChecked)}
                checked={fieldData.showOverlay}
              />
            </FieldInputLabel>
          </Flex>
        </>
      )}
    </Flex>
  );
};

const ScopeOptions = [
  { labelKey: "entirechart", value: "Book" },
  { labelKey: "currentpage", value: "Page" },
  { labelKey: "currentbranch", value: "Branch" }
] as const;

const formatPageSizeDimension = (dimension: number, scaleFactor: number) => {
  return (dimension / scaleFactor).toFixed(1).replace(/\.?0*$/, "");
};

const buildScopeOptions = (
  onChange: (value: Scope) => void,
  scopeController: Nullable<ScopeController>,
  hasActiveBLS: boolean
) => {
  return ScopeOptions.filter(
    option => !hasActiveBLS || !blsRestrictedScopes.includes(option.labelKey)
  ).map(option => ({
    dataTestId: `export-modal-select-scope-${option.value}`,
    disabled: isNullish(scopeController) || scopeController.failedScopes.includes(option.value),
    value: option.value,
    label: appstr(option.labelKey),
    clickHandler: () => onChange(option.value)
  }));
};

const buildPageSizeOptions = (
  mode: ExportModalMode,
  scope: string,
  onChange: (value: string) => void
) => {
  const includePPTSizes = mode === ExportModalMode.PPT;
  let sizes = {};
  if (!includePPTSizes) {
    const filteredSizes = Object.fromEntries(
      Object.entries(enumPageSizes).filter(([key]) => key !== "ppt1ps" && key !== "ppt2ps")
    );
    sizes = filteredSizes;
  } else sizes = enumPageSizes;

  let additionalOptions = {};
  if (scope === "Page") {
    const useEnglish = localStorage.getItem("unitSystem") === "English";
    const scaleFactor = useEnglish ? 96 : 96 / kCMI;
    const unitName = useEnglish ? " in" : " cm";
    const wsMgr = GetWSMgr();
    const doc = wsMgr?.GetDoc();
    const pageMgr = doc?.GetPageMgr();
    const width = pageMgr?.GetMinPageWidth();
    const height = pageMgr?.GetMinPageHeight();
    const pageWidth = formatPageSizeDimension(width, scaleFactor);
    const pageHeight = formatPageSizeDimension(height, scaleFactor);

    additionalOptions = {
      label: `${appstr("curpagesize")} - ${pageHeight} x ${pageWidth}${unitName}`,
      value: "current",
      dataTestId: "publish-scope-current",
      clickHandler: () => onChange("current")
    };
  }
  let options = buildPageSizes(onChange, sizes, "publish-scope", additionalOptions);

  return options;
};

const buildActivityOptions = (onChange: (value: string) => void) => {
  const wsMgr: Nullish<QXWSMgr> = GetWSMgr();
  const planMgr = wsMgr?.GetPlanMgr();

  const options = ["Off", "History"];
  if (!isNullish(planMgr) && planMgr.GetUseEffectiveDates()) {
    options.push("Timeline");
  }

  return options.map(value => ({
    label: appstr(value),
    value: value,
    clickHandler: () => onChange(value)
  }));
};
